import React, { useState } from 'react';
import ProgressStepper from '../../components/ProgressStepper';
import { useStepTitle } from '../../hooks/usePageTitle';
import { submitServiceRequest } from '../../services/serviceRequestApi';
import { createSalesforceCase, isSalesforceAvailable } from '../../services/salesforceService';
import { API_CONFIG } from '../../config';
import { formatPhoneNumber } from '../../utils/helpers';

const CATEGORIES = [
  { group: 'Utility Services', items: ['Meter Issue', 'Power Outage', 'Line Repair', 'Voltage Problem', 'Tree Trimming'] },
  { group: 'Account Services', items: ['Start Service', 'Stop Service', 'Transfer Service', 'Name Change', 'Rate Plan Change'] },
];

const PRIORITIES = ['Low', 'Normal', 'High', 'Emergency'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const STEPS = [
  { label: 'Request Type', description: 'Select category & priority' },
  { label: 'Details', description: 'Describe the issue & location' },
  { label: 'Review & Submit', description: 'Confirm and submit' },
];

const initialFormData = {
  accountNumber: '',
  category: '',
  priority: 'Normal',
  description: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  contactPhone: '',
  contactEmail: '',
  preferredDate: '',
};

const SubmitRequest = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [hasAttemptedNext, setHasAttemptedNext] = useState(false);

  const stepNames = ['Request Type', 'Details', 'Review & Submit'];
  useStepTitle('Service Requests', currentStep, STEPS.length, stepNames[currentStep]);

  // ---- Field helpers ----

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData((prev) => ({ ...prev, contactPhone: raw.length === 10 ? formatPhoneNumber(raw) : e.target.value }));
  };

  const fieldClass = (value, type = 'input') => {
    const base = type === 'select' ? 'form-select' : 'form-control';
    if (!hasAttemptedNext) return base;
    return value.trim() ? base : `${base} is-invalid`;
  };

  // ---- Step validation ----

  const isStep0Valid = () => formData.accountNumber.trim() && formData.category && formData.priority;

  const isStep1Valid = () =>
    formData.description.trim() &&
    formData.street.trim() &&
    formData.city.trim() &&
    formData.state.trim() &&
    formData.postalCode.trim() &&
    formData.contactPhone.trim() &&
    formData.contactEmail.trim();

  const handleNext = () => {
    setHasAttemptedNext(true);
    if (currentStep === 0 && !isStep0Valid()) return;
    if (currentStep === 1 && !isStep1Valid()) return;
    setHasAttemptedNext(false);
    setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    setHasAttemptedNext(false);
    setCurrentStep((s) => s - 1);
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex < currentStep) {
      setHasAttemptedNext(false);
      setCurrentStep(stepIndex);
    }
  };

  // ---- Submit ----

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        accountNumber: formData.accountNumber,
        category: formData.category,
        priority: formData.priority,
        description: formData.description,
        serviceAddress: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
        },
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        preferredDate: formData.preferredDate,
      };

      const result = await submitServiceRequest(payload);

      // Sync to Salesforce Case
      const sfAvailable = isSalesforceAvailable();
      console.log('[TriServe SF] isSalesforceAvailable =', sfAvailable, '| SALESFORCE_API_URL =', JSON.stringify(API_CONFIG.SALESFORCE_API_URL));
      if (sfAvailable) {
        const srId = result?.request?.id || result?.data?.id || result?.id;
        const casePayload = {
          accountNumber: formData.accountNumber,
          requestId: srId,
          category: formData.category,
          priority: formData.priority,
          subject: `Service Request - ${formData.category}`,
          description: formData.description,
        };
        console.log('[TriServe SF] Creating SF Case with payload:', casePayload);
        createSalesforceCase(casePayload)
          .then(res => console.log('[TriServe SF] Case created successfully:', res))
          .catch(err => console.warn('[TriServe SF] Case sync FAILED:', err.message, err));
      }

      setSubmitResult(result);
    } catch (err) {
      setSubmitResult({ success: false, error: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setFormData(initialFormData);
    setCurrentStep(0);
    setSubmitResult(null);
    setHasAttemptedNext(false);
  };

  // ---- Priority badge ----
  const priorityBadge = (p) => {
    const colors = { Low: 'bg-secondary', Normal: 'bg-info', High: 'bg-warning text-dark', Emergency: 'bg-danger' };
    return <span className={`badge ${colors[p] || 'bg-secondary'}`}>{p}</span>;
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  // --- Success state ---
  if (submitResult?.success) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <div className="mb-4">
            <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 80, height: 80 }}>
              <i className="bi bi-check-circle display-4 text-success"></i>
            </div>
          </div>
          <h3 className="h4 text-success mb-2">Service Request Submitted</h3>
          <p className="text-muted mb-1">Your request has been created successfully.</p>
          <p className="mb-4">
            <strong>Request ID:</strong>{' '}
            <span className="badge bg-tse-primary fs-6">{submitResult.request.id}</span>
          </p>

          <div className="card bg-light border-0 mx-auto mb-4" style={{ maxWidth: 480 }}>
            <div className="card-body text-start">
              <div className="row g-2 small">
                <div className="col-5 fw-bold">Category</div><div className="col-7">{submitResult.request.category}</div>
                <div className="col-5 fw-bold">Priority</div><div className="col-7">{priorityBadge(submitResult.request.priority)}</div>
                <div className="col-5 fw-bold">Status</div><div className="col-7"><span className="badge bg-info">Open</span></div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-center gap-2">
            <button className="btn btn-tse-primary" onClick={handleStartOver}>
              <i className="bi bi-plus-circle me-2"></i>New Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (submitResult && !submitResult.success) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <div className="mb-4">
            <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 80, height: 80 }}>
              <i className="bi bi-x-circle display-4 text-danger"></i>
            </div>
          </div>
          <h3 className="h4 text-danger mb-2">Submission Failed</h3>
          <p className="text-muted mb-4">{submitResult.error || 'An unexpected error occurred.'}</p>
          <button className="btn btn-outline-tse-primary" onClick={() => setSubmitResult(null)}>
            <i className="bi bi-arrow-left me-2"></i>Back to Review
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Progress Stepper */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <ProgressStepper
            currentStep={currentStep}
            steps={STEPS}
            onStepClick={handleStepClick}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4">

          {/* ---- STEP 0: Request Type ---- */}
          {currentStep === 0 && (
            <>
              <h4 className="h5 fw-bold mb-4">
                <i className="bi bi-tag me-2 text-warning"></i>Select Request Type
              </h4>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Account Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="accountNumber"
                    className={fieldClass(formData.accountNumber)}
                    placeholder="e.g. TSE-9012-3456"
                    value={formData.accountNumber}
                    onChange={handleChange}
                  />
                  <div className="invalid-feedback">Account number is required.</div>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Priority <span className="text-danger">*</span></label>
                  <select
                    name="priority"
                    className={fieldClass(formData.priority, 'select')}
                    value={formData.priority}
                    onChange={handleChange}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Category <span className="text-danger">*</span></label>
                  {CATEGORIES.map((group) => (
                    <div key={group.group} className="mb-3">
                      <small className="text-muted fw-bold text-uppercase">{group.group}</small>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {group.items.map((cat) => (
                          <button
                            key={cat}
                            type="button"
                            className={`btn btn-sm ${formData.category === cat ? 'btn-tse-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setFormData((prev) => ({ ...prev, category: cat }))}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {hasAttemptedNext && !formData.category && (
                    <div className="text-danger small mt-1">Please select a category.</div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ---- STEP 1: Details ---- */}
          {currentStep === 1 && (
            <>
              <h4 className="h5 fw-bold mb-4">
                <i className="bi bi-card-text me-2 text-warning"></i>Request Details
              </h4>

              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold">Description <span className="text-danger">*</span></label>
                  <textarea
                    name="description"
                    className={fieldClass(formData.description)}
                    rows={4}
                    placeholder="Describe the issue or request in detail..."
                    value={formData.description}
                    onChange={handleChange}
                  />
                  <div className="invalid-feedback">Description is required.</div>
                </div>

                <div className="col-12"><hr className="my-1" /><h6 className="fw-bold mt-2"><i className="bi bi-geo-alt me-1"></i>Service Address</h6></div>

                <div className="col-md-12">
                  <label className="form-label fw-semibold">Street Address <span className="text-danger">*</span></label>
                  <input type="text" name="street" className={fieldClass(formData.street)} placeholder="123 Main St" value={formData.street} onChange={handleChange} />
                  <div className="invalid-feedback">Street address is required.</div>
                </div>

                <div className="col-md-5">
                  <label className="form-label fw-semibold">City <span className="text-danger">*</span></label>
                  <input type="text" name="city" className={fieldClass(formData.city)} placeholder="Charlotte" value={formData.city} onChange={handleChange} />
                  <div className="invalid-feedback">City is required.</div>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">State <span className="text-danger">*</span></label>
                  <select name="state" className={fieldClass(formData.state, 'select')} value={formData.state} onChange={handleChange}>
                    <option value="">Select...</option>
                    {US_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                  <div className="invalid-feedback">State is required.</div>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">ZIP Code <span className="text-danger">*</span></label>
                  <input type="text" name="postalCode" className={fieldClass(formData.postalCode)} placeholder="28205" maxLength={10} value={formData.postalCode} onChange={handleChange} />
                  <div className="invalid-feedback">ZIP code is required.</div>
                </div>

                <div className="col-12"><hr className="my-1" /><h6 className="fw-bold mt-2"><i className="bi bi-telephone me-1"></i>Contact Information</h6></div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Phone <span className="text-danger">*</span></label>
                  <input type="tel" name="contactPhone" className={fieldClass(formData.contactPhone)} placeholder="(555) 123-4567" value={formData.contactPhone} onChange={handlePhoneChange} />
                  <div className="invalid-feedback">Phone number is required.</div>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
                  <input type="email" name="contactEmail" className={fieldClass(formData.contactEmail)} placeholder="customer@email.com" value={formData.contactEmail} onChange={handleChange} />
                  <div className="invalid-feedback">Email address is required.</div>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Preferred Date</label>
                  <input type="date" name="preferredDate" className="form-control" value={formData.preferredDate} onChange={handleChange} />
                  <div className="form-text">Optional — for field visit scheduling</div>
                </div>
              </div>
            </>
          )}

          {/* ---- STEP 2: Review & Submit ---- */}
          {currentStep === 2 && (
            <>
              <h4 className="h5 fw-bold mb-4">
                <i className="bi bi-clipboard-check me-2 text-warning"></i>Review Your Request
              </h4>

              <div className="row g-4">
                {/* Request Info */}
                <div className="col-md-6">
                  <div className="card bg-light border-0">
                    <div className="card-body">
                      <h6 className="fw-bold mb-3"><i className="bi bi-tag me-1 text-tse-primary"></i>Request Information</h6>
                      <div className="row g-2 small">
                        <div className="col-5 fw-bold">Account</div><div className="col-7">{formData.accountNumber}</div>
                        <div className="col-5 fw-bold">Category</div><div className="col-7">{formData.category}</div>
                        <div className="col-5 fw-bold">Priority</div><div className="col-7">{priorityBadge(formData.priority)}</div>
                        {formData.preferredDate && (<><div className="col-5 fw-bold">Preferred Date</div><div className="col-7">{formData.preferredDate}</div></>)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address & Contact */}
                <div className="col-md-6">
                  <div className="card bg-light border-0">
                    <div className="card-body">
                      <h6 className="fw-bold mb-3"><i className="bi bi-geo-alt me-1 text-tse-primary"></i>Service Location & Contact</h6>
                      <div className="row g-2 small">
                        <div className="col-5 fw-bold">Address</div>
                        <div className="col-7">{formData.street}<br />{formData.city}, {formData.state} {formData.postalCode}</div>
                        <div className="col-5 fw-bold">Phone</div><div className="col-7">{formData.contactPhone}</div>
                        <div className="col-5 fw-bold">Email</div><div className="col-7">{formData.contactEmail}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <div className="card bg-light border-0">
                    <div className="card-body">
                      <h6 className="fw-bold mb-2"><i className="bi bi-card-text me-1 text-tse-primary"></i>Description</h6>
                      <p className="mb-0 small">{formData.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ---- Navigation Buttons ---- */}
          <div className="d-flex justify-content-between mt-4 pt-3 border-top">
            {currentStep > 0 ? (
              <button className="btn btn-outline-secondary" onClick={handleBack}>
                <i className="bi bi-arrow-left me-2"></i>Back
              </button>
            ) : (
              <div></div>
            )}

            {currentStep < STEPS.length - 1 ? (
              <button className="btn btn-tse-primary" onClick={handleNext}>
                Next<i className="bi bi-arrow-right ms-2"></i>
              </button>
            ) : (
              <button className="btn btn-success px-4" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Submitting...</>
                ) : (
                  <><i className="bi bi-send me-2"></i>Submit Request</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmitRequest;
